import React, { useState } from "react";

const TwoFALoginModal = ({ show, onClose, onVerify, tempUserId }) => {
  const [token, setToken] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch("http://localhost:5000/api/auth/login-2fa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tempUserId,
          [useRecoveryCode ? "recoveryCode" : "token"]: token.toUpperCase(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        onVerify(data);
      } else {
        setError(data.error || "Verification failed");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits for token
    if (useRecoveryCode) {
      setToken(e.target.value.toUpperCase());
    } else {
      setToken(value);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
      <div className="w-full max-w-md rounded-2xl border-2 border-primary/20 bg-[var(--bg-card)] p-8 shadow-2xl shadow-primary/10">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#22c55e] to-[#16a34a] flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent mb-2">
            Two-Factor Authentication
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">
            {useRecoveryCode
              ? "Enter your 8-character recovery code"
              : "Enter the 6-digit code from your authenticator app"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <input
              type="text"
              value={token}
              onChange={handleInputChange}
              placeholder={useRecoveryCode ? "A1B2C3D4" : "000000"}
              maxLength={useRecoveryCode ? 8 : 6}
              className="w-full rounded-xl border-2 border-primary bg-surface-softer px-4 py-4 text-center text-3xl font-bold tracking-[0.5em] text-[var(--text-primary)] transition-all placeholder:text-[var(--text-secondary)]/50 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/20"
              required
              autoFocus
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border-2 border-red-500 rounded-xl">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-red-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-red-500 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3">
            <button
              type="submit"
              disabled={
                loading ||
                (useRecoveryCode ? token.length !== 8 : token.length !== 6)
              }
              className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold text-lg hover:shadow-lg hover:shadow-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>Verifying...</span>
                </div>
              ) : (
                "Verify & Login"
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl border border-[var(--border-color)] bg-surface-softer px-6 py-4 font-semibold text-[var(--text-primary)] transition-all hover:bg-surface-soft"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Toggle Recovery Code */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              setUseRecoveryCode(!useRecoveryCode);
              setToken("");
              setError("");
            }}
            className="text-primary hover:text-primary/80 underline text-sm font-medium transition-colors"
          >
            {useRecoveryCode
              ? "← Use authenticator code"
              : "Lost your device? Use recovery code →"}
          </button>
        </div>

        {/* Help Text */}
        {!useRecoveryCode && (
          <div className="mt-4 rounded-lg border border-[var(--border-color)] bg-surface-softer p-3">
            <p className="text-center text-xs text-[var(--text-secondary)]">
              💡 Codes refresh every 30 seconds. Make sure your device time is
              accurate.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFALoginModal;
